/**
 * SpaceDetail - Integration in One Editorial Workspace
 *
 * Migrated 2026-05-17 to the Integration-in-One design system.
 * Pattern: editorial page-head + 3-column card grid with hover frosted-glass
 * action bar + Create/Edit-Modal with tabs.
 *
 * Tokens scoped via .iio wrapper (App.jsx mounts it on the app shell).
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "../utils/helpers";
import { readContentCounts } from "../utils/contentCounts";
import { useToast, ToastContainer } from "../components/ui";
import { useApiQuery } from "../hooks/useApiQuery";
import { TAG_ZU_STUFE, STAGES, stageLabel } from "../utils/stages";
import InstanceIcon from "../components/InstanceIcon";
import IconPicker from "../components/IconPicker";
import PolicyCredentialSource from "../components/PolicyCredentialSource";
import PolicyCredentials, { policyTransportError } from "../components/PolicyCredentials";
import RuntimeLocationsField from "../components/RuntimeLocationsField";
import SpaceWerkeTile from "../components/SpaceWerkeTile";
import { Typography } from "@mui/material";

// Groessenangabe. Bewusst einmal fuer Dialog UND Karte: zwei Formatierer waeren zwei
// Wahrheiten fuer dieselbe Zahl.
const fmtBytes = (b) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB`
  : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b || 0} B`;

// --- Inline SVG icons ---------------------------------------------------
const TestIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
const BugIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V4a2 2 0 012-2h8.5L20 7.5V20a2 2 0 01-2 2H6a2 2 0 01-2-2v-3"/><polyline points="14 2 14 8 20 8"/><path d="M2 15h10"/><path d="M8 11l4 4-4 4"/></svg>;
const ShieldIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const GlobeIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>;
const ExternalIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
const PencilIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const MoreIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>;
const SpinIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{animation:"iio-spin 1s linear infinite"}}><path d="M21 12a9 9 0 11-6.22-8.56"/></svg>;
const CloseIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const PlusIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const BackIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;

// Welche Stufe traegt die Instanz? Gleiche Regel wie stageFromTags im Backend: erster Tag, der
// dev/qas/prod trifft, Gross- und Kleinschreibung egal. Bestimmt, in welches GitHub-Environment
// die Zugangsdaten beim Speichern gehen.
// Dritte Kopie derselben Logik - jetzt aus utils/stages, damit eine neue Stufe nicht an drei
// Orten nachgetragen werden muss. Erkennt auch die Schreibweisen (pre-prod, staging, test ...).
function stufeAusTags(tags) {
  for (const t of (Array.isArray(tags) ? tags : [])) {
    const k = String(t || "").trim().toLowerCase();
    const treffer = TAG_ZU_STUFE[k];
    if (treffer) return treffer;
  }
  return null;
}
// Sachtags = alles, was keine Stufe ist. Die Stufe hat ein eigenes Bedienelement und darf
// nicht zusaetzlich als Freitext auftauchen, sonst stuenden zwei Wege zum selben Ziel offen.
function sachtagsVon(tags) {
  return (Array.isArray(tags) ? tags : []).filter(t => !TAG_ZU_STUFE[String(t || "").trim().toLowerCase()]);
}

// Das Backend meldet in res.cicd, ob beim Speichern Zugangsdaten in ein GitHub-Environment
// gegangen sind. Ein stiller Push waere die schlechteste Rueckmeldung, deshalb steht das hier
// als EINE Funktion fuer beide Wege (Anlegen und Bearbeiten) - beim Anlegen fehlte sie zuerst.
function cicdPushMeldung(res, t) {
  const c = res?.cicd;
  if (!c) return null;
  const felder = Array.isArray(c.felder) && c.felder.length ? c.felder.join(", ") : t("meldung.cicdFelder");
  const env = c.env || "?";
  return c.gepusht
    ? { text: c.repo ? t("meldung.cicdGepushtRepo", { env, repo: c.repo, felder }) : t("meldung.cicdGepusht", { env, felder }), art: "success", dauer: 6500 }
    // Die Beruhigung zuerst: die Eingabe des Nutzers ist sicher, nur GitHub hinkt hinterher.
    : { text: t("meldung.cicdFehler", { grund: c.fehler || t("meldung.cicdGrundUnbekannt") }), art: "error", dauer: 9000 };
}

const EMPTY_FORM = {
  name: "", description: "", tags: ["", "", ""], stufe: "",
  tenantUrl: "", authType: "oauth",
  clientId: "", clientSecret: "", tokenUrl: "",
  username: "", password: "",
  monitorHours: 24, suiteUrl: "",
  runtimeLocations: [], edgeMonitoring: [],
  hasPipelineConcept: false,
  accessPolicyEnforcement: false,
  policyTargetAuth: false, policyUsername: "", policyPassword: "", policyCredentials: [], policyTransportKey: null,
  aiCapabilities: { enabled: false, features: { read: true, write: false, deploy: false, monitor: true, test: false, tpm: false } },
  icon: "layers",
};

// --- Edge-Deploy-Tester -------------------------------------------------
// ⛔ Wird seit dem Aufraeumen der Instanz-Einstellungen (01.08.2026) NICHT mehr gerendert.
// Der Aufruf im Reiter "Werke" ist auskommentiert, die Komponente bleibt absichtlich stehen.
// In den Instanz-Einstellungen: Package + iFlow waehlen und testweise in eine
// Runtime (Cloud oder konfigurierte Edge Integration Cell) deployen - zum
// Verproben. Nutzt bestehende Endpunkte: /content/packages, /flows, /deploy,
// /runtime-status. Nur fuer bereits gespeicherte Instanzen (braucht instanceId).
function EdgeDeployTester({ instanceId, runtimeLocations }) {
  const { t } = useTranslation(["space", "gemeinsam"]);
  const [pkgs, setPkgs] = useState([]);
  const [pkgId, setPkgId] = useState("");
  const [flows, setFlows] = useState([]);
  const [flowId, setFlowId] = useState("");
  const [loc, setLoc] = useState("");
  const [loadingPkgs, setLoadingPkgs] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const edges = (Array.isArray(runtimeLocations) ? runtimeLocations : []).filter(r => r && r.locationId);
  const edgeKey = edges.map(e => e.locationId).join("|");

  // Default-Ziel: erste konfigurierte Edge (sonst Cloud Default).
  useEffect(() => {
    if (edges.length) setLoc(prev => (prev && edges.some(e => e.locationId === prev)) ? prev : edges[0].locationId);
    else setLoc("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeKey]);

  // Pakete des Tenants laden.
  useEffect(() => {
    let alive = true;
    setLoadingPkgs(true);
    api(`/api/${instanceId}/content/packages`)
      .then(d => { if (alive) setPkgs(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setPkgs([]); })
      .finally(() => { if (alive) setLoadingPkgs(false); });
    return () => { alive = false; };
  }, [instanceId]);

  // iFlows des gewaehlten Pakets laden.
  useEffect(() => {
    if (!pkgId) { setFlows([]); setFlowId(""); return; }
    let alive = true;
    setLoadingFlows(true); setFlowId(""); setResult(null);
    api(`/api/${instanceId}/content/packages/${encodeURIComponent(pkgId)}/flows`)
      .then(d => { if (alive) setFlows(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setFlows([]); })
      .finally(() => { if (alive) setLoadingFlows(false); });
    return () => { alive = false; };
  }, [instanceId, pkgId]);

  const runDeploy = async () => {
    if (!flowId) return;
    setDeploying(true); setResult(null);
    const locParam = loc ? `?locationId=${encodeURIComponent(loc)}` : "";
    try {
      await api(`/api/${instanceId}/deploy/${encodeURIComponent(flowId)}${locParam}`, { method: "POST" });
      // Status pollen bis terminal (STARTED/ERROR) oder Timeout (~20s).
      let status = "UNKNOWN";
      for (let i = 0; i < 8; i++) {
        await new Promise(r => setTimeout(r, 2500));
        if (!mounted.current) return;
        try {
          const st = await api(`/api/${instanceId}/runtime-status/${encodeURIComponent(flowId)}${locParam}`);
          status = st?.status || (st?.deployed === false ? "NOT_DEPLOYED" : "UNKNOWN");
          if (status === "STARTED" || status === "ERROR") break;
        } catch { /* weiter versuchen */ }
      }
      if (mounted.current) setResult({ ok: status === "STARTED", status });
    } catch (e) {
      if (mounted.current) setResult({ ok: false, error: e?.message || t("edgeTest.deployFehler") });
    } finally {
      if (mounted.current) setDeploying(false);
    }
  };

  const targetName = loc ? (edges.find(e => e.locationId === loc)?.name || loc) : t("edgeTest.cloud");

  return (
    <div className="iio-field" style={{ marginTop: 4, paddingTop: 16, borderTop: "1px solid var(--iio-border)" }}>
      <span className="iio-field-label">{t("edgeTest.titel")}</span>
      <div style={{ fontSize: 12, color: "var(--iio-text-2)", marginBottom: 10, lineHeight: 1.5 }}>
        {t("edgeTest.info")}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        <div className="iio-field" style={{ margin: 0 }}>
          <span className="iio-field-label">{t("edgeTest.package")}</span>
          <select className="iio-input" value={pkgId} onChange={e => setPkgId(e.target.value)} disabled={loadingPkgs}>
            <option value="">{loadingPkgs ? t("edgeTest.paketeLaden") : t("edgeTest.paketWaehlen")}</option>
            {pkgs.map(p => <option key={p.Id} value={p.Id}>{p.Name || p.Id}</option>)}
          </select>
        </div>
        <div className="iio-field" style={{ margin: 0 }}>
          <span className="iio-field-label">{t("edgeTest.iflow")}</span>
          <select className="iio-input" value={flowId} onChange={e => { setFlowId(e.target.value); setResult(null); }} disabled={!pkgId || loadingFlows}>
            <option value="">{loadingFlows ? t("edgeTest.iflowsLaden") : (!pkgId ? t("edgeTest.erstPaket") : t("edgeTest.iflowWaehlen"))}</option>
            {flows.map(f => <option key={f.Id} value={f.Id}>{f.Name || f.Id}</option>)}
          </select>
        </div>
        <div className="iio-field" style={{ margin: 0 }}>
          <span className="iio-field-label">{t("edgeTest.ziel")}</span>
          <select className="iio-input" value={loc} onChange={e => { setLoc(e.target.value); setResult(null); }}>
            <option value="">{t("edgeTest.cloud")}</option>
            {edges.map(e => <option key={e.locationId} value={e.locationId}>{e.name || e.locationId}</option>)}
          </select>
          {!edges.length && <div className="iio-field-hint" style={{ marginTop: 6 }}>{t("edgeTest.keineEdge")}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 2, flexWrap: "wrap" }}>
          <button type="button" className="iio-btn iio-btn--secondary" onClick={runDeploy} disabled={!flowId || deploying}>
            {deploying ? t("edgeTest.laeuft") : t("edgeTest.starten", { ziel: targetName })}
          </button>
          {result && (
            result.ok
              ? <span style={{ fontSize: 13, color: "var(--iio-success)", fontWeight: 600 }}>{t("edgeTest.erfolg", { status: result.status })}</span>
              : <span style={{ fontSize: 13, color: "var(--iio-critical)", fontWeight: 600 }}>{result.error ? t("edgeTest.fehlgeschlagen", { grund: result.error }) : t("edgeTest.status", { status: result.status })}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// CI/CD-Symbole, exakt gleich wie in SpacesHome/Sidebar - konsistente Bildsprache
// bedeutet, derselbe Zustand traegt ueberall dasselbe Symbol.
const CicdReadyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12h4l2-3 4 6 2-3h4"/><circle cx="12" cy="12" r="9"/>
  </svg>
);
const CicdOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9"/><path d="M12 8v4"/><path d="M12 16h.01"/>
  </svg>
);
const CicdWarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const SpaceDetail = ({ space, cicdStatus, onBack, onSpaceUpdated, onOpenInstance, onInstanceUpdated }) => {
  const { t, i18n } = useTranslation(["space", "gemeinsam", "spaces"]);
  // Zahlen folgen der gewaehlten Sprache. Ein fest eingestelltes "de-DE" verfaelscht die Zahl
  // selbst: "18.859" liest sich im Englischen als achtzehn Komma acht.
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const zahl = (x) => (x ?? 0).toLocaleString(gebiet);
  const { toasts, addToast } = useToast();
  const [instances, setInstances] = useState([]);
  const [stats, setStats] = useState({});
  const [openMenuFor, setOpenMenuFor] = useState(null);
  // Rolle fuer diesen Space (aus /api/spaces): owner | admin | viewer. Steuert sichtbare Aktionen.
  const sRole = space?.role || "owner";
  const isOwnerSpace = !space?.isShared && sRole === "owner";
  const canManage = isOwnerSpace || sRole === "admin"; // darf Instanzen verwalten/bearbeiten

  // --- Modal state -------------------------------------------------------
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [modalTab, setModalTab] = useState("connection");
  // Index des aufgeklappten Edge-Monitoring-Eintrags im Dialog (null = alle zu)
  const [edgeMonOffen, setEdgeMonOffen] = useState(null);
  // Info zum Modus für hohe Durchsätze. Bewusst aufklappbar und nicht als Hover-Tooltip:
  // .iio-modal scrollt (overflow:auto), ein absolut positionierter Tooltip würde dort
  // abgeschnitten. Aufklappen funktioniert zudem per Tastatur und auf dem Touchgerät.
  // JMS-Queues je Instanz. Wird NICHT beim Seitenaufbau geladen: bei vielen Instanzen waere
  // das ein Tenant-Aufruf pro Karte. Erst auf Anforderung, serverseitig 30s zwischengespeichert.
  const [queueModal, setQueueModal] = useState(null);
  const [queueFilter, setQueueFilter] = useState("");
  const [nurGefuellte, setNurGefuellte] = useState(false);
  const [queueAuswahl, setQueueAuswahl] = useState([]);   // Auswahl im Dialog, ungespeichert
  const [queueSpeichert, setQueueSpeichert] = useState(false);
  // Fuellstaende der angepinnten Queues. Wird NUR fuer Instanzen geladen, die welche haben -
  // die Last entsteht also erst, wenn der Nutzer sich dafuer entschieden hat.
  const [pinStand, setPinStand] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [delConfirm, setDelConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [testingInModal, setTestingInModal] = useState(false);
  // Action-Result-Modal - zeigt Output von Test/Permission/Ping/Debug
  const [actionResult, setActionResult] = useState(null); // { type, instId, instName, loading, data, error }
  const [busyAction, setBusyAction] = useState({}); // { [instId]: actionType }

  // --- TanStack dual-mode for instance list ------------------------------
  const { isOptimized } = useApiQuery(
    ["space-instances", space?.id],
    `/api/spaces/${space?.id}/instances`,
    { staleTime: 60000, refetchInterval: 120000, enabled: !!space?.id }
  );

  const contentReads = useRef(null);
  const load = useCallback(async () => {
    if (!space?.id) return;
    contentReads.current?.abort();
    const controller = new AbortController(); contentReads.current = controller;
    try {
      const insts = await api(`/api/spaces/${space.id}/instances`);
      if (controller.signal.aborted) return;
      setInstances(insts);
      for (const inst of insts) {
        api(`/api/instances/${inst.id}/stats`).then(s => {
          if (controller.signal.aborted) return;
          setStats(prev => ({ ...prev, [inst.id]: s }));
          if (s._contentSource === "loading") {
            readContentCounts(inst.id, { signal: controller.signal }).then(c => {
              if (!controller.signal.aborted && Number.isFinite(c?.packageCount) && Number.isFinite(c?.artifactCount)) {
                setStats(prev => ({ ...prev, [inst.id]: { ...prev[inst.id], packageCount: c.packageCount, artifactCount: c.artifactCount,
                  _contentRuntimeOnly: !!c.runtimeOnly, _contentStale: !!c.stale } }));
              }
            }).catch(() => {});
          }
        }).catch(() => {});
      }
    } catch {}
  }, [space?.id]);

  useEffect(() => { load(); return () => contentReads.current?.abort(); }, [load, isOptimized]);

  // ESC closes any open modal/menu
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showCreate) closeModal();
      else if (editing) closeModal();
      else if (openMenuFor) setOpenMenuFor(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // Outside-click closes more-menu
  useEffect(() => {
    if (!openMenuFor) return;
    const onClick = (e) => {
      if (!e.target.closest?.(".iio-card-menu, [data-more-trigger]")) setOpenMenuFor(null);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [openMenuFor]);

  // --- Form handlers -----------------------------------------------------
  // Aus einer Vorlage abgeleitet? Dann gehört die Verbindung der Vorlage und ist hier gesperrt.
  const istGeerbt = !!form.serviceKeyVon;
  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setTag = (idx, value) => setForm(prev => {
    const tags = [...(prev.tags || ["", "", ""])];
    tags[idx] = value;
    return { ...prev, tags };
  });
  const setEdgeMonFeld = (idx, feld, wert) => setForm(prev => {
    const next = [...(prev.edgeMonitoring || [])];
    next[idx] = { ...next[idx], [feld]: wert };
    return { ...prev, edgeMonitoring: next };
  });

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setModalTab("json");
    setEdgeMonOffen(null);
    setShowCreate(true);
  };
  // Fuellstand der angepinnten Queues nachladen. Laeuft nur fuer Instanzen mit Pins, damit
  // der Seitenaufbau bei vielen Instanzen keine Tenant-Aufrufe ausloest.
  useEffect(() => {
    for (const i of instances) {
      if (Array.isArray(i.pinnedQueues) && i.pinnedQueues.length && pinStand[i.id] === undefined) {
        ladePinStand(i.id, i.pinnedQueues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances]);

  const openQueues = async (inst) => {
    setQueueModal({ inst, loading: true });
    setQueueFilter(""); setNurGefuellte(false);
    setQueueAuswahl(Array.isArray(inst.pinnedQueues) ? [...inst.pinnedQueues] : []);
    try {
      const d = await api(`/api/${inst.id}/jms-queues`);
      setQueueModal({ inst, loading: false, data: d });
    } catch (e) {
      setQueueModal({ inst, loading: false, error: e.reason || e.message });
    }
  };

  const QUEUE_MAX = 4;
  const toggleQueue = (name) => {
    setQueueAuswahl(alt => alt.includes(name)
      ? alt.filter(q => q !== name)
      : alt.length >= QUEUE_MAX ? alt : [...alt, name]);
  };
  const speichereQueues = async () => {
    if (!queueModal?.inst) return;
    setQueueSpeichert(true);
    try {
      // api() reicht body unveraendert an fetch weiter - hier MUSS serialisiert werden,
      // sonst kommt "[object Object]" am Server an. Konvention der uebrigen Aufrufe.
      await api(`/api/${queueModal.inst.id}/pinned-queues`, { method: "PUT", body: JSON.stringify({ queues: queueAuswahl }) });
      setInstances(list => list.map(i => i.id === queueModal.inst.id ? { ...i, pinnedQueues: [...queueAuswahl] } : i));
      setQueueModal(null);
      ladePinStand(queueModal.inst.id, queueAuswahl);
    } catch (e) {
      setQueueModal(m => ({ ...m, error: e.reason || e.message }));
    } finally { setQueueSpeichert(false); }
  };
  const ladePinStand = async (instId, namen) => {
    if (!namen?.length) { setPinStand(p => ({ ...p, [instId]: null })); return; }
    try {
      const d = await api(`/api/${instId}/jms-queues`);
      const map = {};
      for (const q of (d.queues || [])) map[q.name] = q;
      setPinStand(p => ({ ...p, [instId]: { map, nichtVerfuegbar: d.nichtVerfuegbar === true } }));
    } catch { setPinStand(p => ({ ...p, [instId]: { map: {}, fehler: true } })); }
  };

  const openEdit = (inst) => {
    const nextForm = {
      ...EMPTY_FORM,
      ...inst,
      description: inst.description || "",
      // Stufe und Sachtags getrennt fuehren: die Stufe waehlt ein Dropdown, die Sachtags
      // bleiben Freitext. Beim Speichern werden sie wieder zu EINER tags-Liste zusammengefuegt.
      // Bestehende Instanzen brauchen dafuer nichts - ihr vorhandener Tag wird hier erkannt
      // und vorbelegt, egal in welcher Schreibweise (dev, DEV, development, test, ...).
      stufe: stufeAusTags(inst.tags) || "",
      serviceKeyVon: inst.serviceKeyVon || null,
      vorlageName: inst.vorlageName || null,
      tags: [...sachtagsVon(inst.tags), "", "", "", ""].slice(0, 4),
      // Don't pre-fill secrets - empty means "unchanged"
      clientSecret: "",
      password: "",
      policyPassword: "",
      policyTargetAuth: inst.policyTargetAuth || false,
      policyUsername: inst.policyUsername || "",
      policyCredentials: Array.isArray(inst.policyCredentials) && inst.policyCredentials.length > 0
        ? inst.policyCredentials.map(c => ({ key: c.key || "", username: c.username || "", password: "" }))
        : (inst.policyUsername ? [{ key: "", username: inst.policyUsername, password: "" }] : []),
      // Secrets kommen nie mit vom Server - leer heisst "unveraendert", hasSecret sagt dem
      // Formular, dass ein gespeichertes Secret existiert (steuert nur den Platzhalter).
      edgeMonitoring: Array.isArray(inst.edgeMonitoring)
        ? inst.edgeMonitoring.map(e => ({ id: e.id, name: e.name || "", url: e.url || "", clientId: e.clientId || "", tokenUrl: e.tokenUrl || "", clientSecret: "", hasSecret: !!e.hasSecret }))
        : [],
      aiCapabilities: inst.aiCapabilities || EMPTY_FORM.aiCapabilities,
      icon: inst.icon || "layers",
    };
    setForm(nextForm);
    setInitialForm(nextForm);
    setEditing(inst);
    setModalTab("connection");
    setEdgeMonOffen(null);
    setDelConfirm("");
    setOpenMenuFor(null);
  };
  const closeModal = () => {
    setShowCreate(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setEdgeMonOffen(null);
    setDelConfirm("");
    setSubmitting(false);
    setTestingInModal(false);
  };

  const onlyIconEdit = !!editing && (!canManage || (initialForm &&
    JSON.stringify({ ...form, icon: initialForm.icon }) === JSON.stringify(initialForm)));
  const publishIcon = (inst, icon) => {
    setInstances(list => list.map(item => item.id === inst.id ? { ...item, icon } : item));
    onInstanceUpdated?.({ ...inst, icon });
  };
  const submitForm = async () => {
    if (onlyIconEdit) {
      if (form.icon === (editing.icon || "layers")) { closeModal(); return; }
      setSubmitting(true);
      try {
        const result = await api(`/api/instances/${editing.id}/icon`, { method: "PUT", body: JSON.stringify({ icon: form.icon }) });
        publishIcon(editing, result.icon || form.icon);
        addToast(t("meldung.instanzAktualisiert"), "success");
        closeModal();
      } catch (e) { addToast(e.message, "error"); }
      finally { setSubmitting(false); }
      return;
    }
    if (!form.name.trim() || !form.tenantUrl.trim()) {
      addToast(t("meldung.pflichtfelder"), "error");
      return;
    }
    const transportError = !form.policyCredentialSource && policyTransportError(form.policyCredentials || [], form.policyTransportKey, form.policyTargetAuth);
    if (transportError) {
      setModalTab("policies");
      addToast(t(transportError), "error");
      return;
    }
    // Edge-Monitoring-Eintraege: komplett leere Zeilen verschwinden still, teilausgefuellte
    // wuerden das Backend ablehnen - deshalb hier die Meldung, solange der Dialog offen ist.
    const edgeEintraege = (Array.isArray(form.edgeMonitoring) ? form.edgeMonitoring : [])
      .map(e => ({ id: e.id, name: (e.name || "").trim(), url: (e.url || "").trim(), clientId: (e.clientId || "").trim(), tokenUrl: (e.tokenUrl || "").trim(), clientSecret: e.clientSecret || "" }))
      .filter(e => e.name || e.url || e.clientId || e.clientSecret);
    if (edgeEintraege.some(e => !e.name || !e.url)) {
      setModalTab("edge");
      addToast(t("meldung.edgeUnvollstaendig"), "error");
      return;
    }
    setSubmitting(true);
    try {
      // Sachtags und Stufe wieder zu EINER Liste. Die Stufe steht hinten, damit sie in der
      // Anzeige nicht die fachlichen Marker verdraengt.
      const sach = (form.tags || []).map(tag => (tag || "").trim()).filter(Boolean).filter(tag => !TAG_ZU_STUFE[tag.toLowerCase()]).slice(0, 4);
      const cleanTags = form.stufe ? [...sach, form.stufe] : sach;
      // Runtime-Locations (Edge Integration Cells) saeubern: leere Zeilen raus,
      // trimmen, Name faellt auf die Location-ID zurueck. Shape {name, locationId}
      // - exakt was /api/:iid/runtimes und der Transport-Deploy erwarten.
      const cleanRuntimeLocations = (Array.isArray(form.runtimeLocations) ? form.runtimeLocations : [])
        .map(r => (typeof r === "string"
          ? { name: r.trim(), locationId: r.trim() }
          : { name: (r?.name || "").trim(), locationId: (r?.locationId || "").trim() }))
        .filter(r => r.locationId)
        .map(r => ({ name: r.name || r.locationId, locationId: r.locationId }));
      const payload = { ...form, tags: cleanTags, runtimeLocations: cleanRuntimeLocations, edgeMonitoring: edgeEintraege };
      // Die Werke gehören seit Welle 5 dem Space (Kachel „Werke“). Der Instanzdialog schreibt sie nicht
      // mehr: ein Bearbeiten lässt instance.werke unverändert stehen (Rückfall), eine neue Instanz
      // bekommt keine eigene Liste. Das Formular trägt die effektive Liste nur noch mit.
      delete payload.werke;
      // The source may have changed while this dialog was open. Never resubmit its projection.
      if (form.serviceKeyVon || form.runtimeSource || form.runtimeInheritanceError) delete payload.runtimeLocations;
      delete payload.runtimeSource;
      delete payload.runtimeInheritanceError;
      // Username-Felder trimmen damit Copy/Paste-Whitespace nicht Auth bricht.
      // Passwoerter NICHT trimmen (Whitespace darin ist valide).
      // Region nur senden, wenn ein Wert gesetzt ist oder ein bestehender entfernt wurde.
      // Das Backend normalisiert (Großbuchstaben); ohne Eingabe bleibt die Payload unverändert.
      // Abgeleitete Instanzen übernehmen die Region live aus der Vorlage, nie senden.
      if (form.serviceKeyVon) delete payload.region;
      if (payload.region !== undefined) {
        payload.region = String(payload.region || "").trim();
        if (!payload.region && !editing?.region) delete payload.region;
      }
      if (payload.policyUsername) payload.policyUsername = String(payload.policyUsername).trim();
      if (payload.clientId) payload.clientId = String(payload.clientId).trim();
      // Don't send empty secret on edit (means "unchanged")
      if (editing) {
        if (!payload.clientSecret) delete payload.clientSecret;
        if (!payload.password) delete payload.password;
        if (!payload.policyPassword) delete payload.policyPassword;
      }
      if (payload.policyCredentials) {
        payload.policyCredentials = payload.policyCredentials.filter(c => c.username.trim()).map(c => ({
          key: (c.key || "").trim(), username: (c.username || "").trim(), password: c.password || "",
        }));
        // The backend derives legacy aliases from the transport selection.
        payload.policyUsername = "";
        delete payload.policyPassword;
      }
      if (payload.policyCredentialSource) {
        for (const key of ["policyTargetAuth", "policyUsername", "policyPassword", "policyCredentials", "policyTransportKey"]) delete payload[key];
      }
      if (editing) {
        const res = await api(`/api/instances/${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
        publishIcon(editing, res.icon || form.icon);
        addToast(t("meldung.instanzAktualisiert"), "success");
        { const m = cicdPushMeldung(res, t); if (m) addToast(m.text, m.art, m.dauer); }
        if (res.policySync?.some(result => !result.gepusht)) addToast(t("policySource.syncFailed"), "error", 12000);
      } else {
        // Auch hier die Antwort abgreifen: eine NEUE Instanz mit Stufen-Tag in einem gebundenen
        // Space schickt ihre Zugangsdaten ebenfalls nach GitHub. Vorher wurde die Antwort hier
        // verworfen, der Push blieb also unsichtbar.
        const res = await api(`/api/spaces/${space.id}/instances`, { method: "POST", body: JSON.stringify(payload) });
        addToast(t("meldung.instanzErstellt"), "success");
        { const m = cicdPushMeldung(res, t); if (m) addToast(m.text, m.art, m.dauer); }
      }
      closeModal();
      load();
    } catch (e) {
      addToast(e.message, "error");
    }
    setSubmitting(false);
  };

  const submitDelete = async () => {
    if (!editing || delConfirm !== editing.name) return;
    try {
      await api(`/api/instances/${editing.id}`, { method: "DELETE" });
      addToast(t("meldung.instanzGeloescht"), "success");
      closeModal();
      load();
    } catch (e) {
      addToast(e.message, "error");
    }
  };

  // --- Card hover actions -----------------------------------------------
  const setBusy = (instId, type) => setBusyAction(prev => type ? { ...prev, [instId]: type } : (() => { const n = { ...prev }; delete n[instId]; return n; })());

  // Generic action runner - öffnet Result-Modal mit Loading, dann mit data
  const runInstanceAction = async ({ inst, type, label, method, url, body, transform }) => {
    setBusy(inst.id, type);
    setActionResult({ type, instId: inst.id, instName: inst.name, label, loading: true, data: null, error: null });
    try {
      const opts = { method: method || "GET" };
      if (body) opts.body = JSON.stringify(body);
      const r = await api(url, opts);
      const data = transform ? transform(r) : r;
      setActionResult({ type, instId: inst.id, instName: inst.name, label, loading: false, data, error: null });
      // Optional side-effect: refresh stats wenn debug-counts neue Counts liefert
      if (type === "debug" && r?.result) {
        setStats(prev => ({ ...prev, [inst.id]: { ...prev[inst.id], packageCount: r.result.packageCount, artifactCount: r.result.artifactCount,
          _contentRuntimeOnly: !!r.result.runtimeOnly, _contentStale: !!r.result.stale } }));
      }
    } catch (e) {
      setActionResult({ type, instId: inst.id, instName: inst.name, label, loading: false, data: null, error: e.message || t("gemeinsam:zustand.fehler") });
    }
    setBusy(inst.id, null);
  };

  const testConnection = (inst) => runInstanceAction({
    inst, type: "test", label: t("aktion.test"),
    method: "POST", url: `/api/instances/${inst.id}/test`,
  });
  const runPermCheck = (inst) => runInstanceAction({
    inst, type: "perm", label: t("aktion.perm"),
    method: "POST", url: `/api/instances/${inst.id}/permission-check`,
  });
  const pingEnvironments = (inst) => runInstanceAction({
    inst, type: "ping", label: t("aktion.ping"),
    method: "POST", url: `/api/instances/${inst.id}/discover-runtimes`,
    // Konfigurierte Edge Integration Cells mitschicken, damit der Discover-Endpoint
    // sie via /location/<id>/ tatsaechlich anpingt (sonst wird nur Cloud geprueft).
    body: { edgeIds: (Array.isArray(inst.runtimeLocations) ? inst.runtimeLocations : []).map(r => r?.locationId).filter(Boolean) },
  });
  const reloadArtifacts = (inst) => runInstanceAction({
    inst, type: "debug", label: t("aktion.debug"),
    method: "GET", url: `/api/instances/${inst.id}/debug-counts`,
  });
  const closeActionResult = () => setActionResult(null);

  const openCockpit = (inst) => {
    const url = inst.suiteUrl || inst.tenantUrl;
    if (url) window.open(url, "_blank", "noopener");
    else addToast(t("meldung.keineCockpitUrl"), "error");
  };
  const goToInstance = (inst) => onOpenInstance(inst, null);

  // --- Render ------------------------------------------------------------
  return (
    <>
      <ToastContainer toasts={toasts}/>

      {/* Page head */}
      <div className="iio-page-head iio-anim-glide">
        <div className="iio-page-head-left iio-anim-arrival" style={{ animationDelay: "60ms" }}>
          <div className="iio-breadcrumb">
            <button type="button" onClick={onBack} style={{ background: "transparent", border: 0, padding: 0, font: "inherit", color: "var(--iio-text-3)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <BackIcon/><span>{t("kopf.brotkrume")}</span>
            </button>
            <span className="iio-sep">/</span>
            <button type="button" onClick={onBack} style={{ background: "transparent", border: 0, padding: 0, font: "inherit", color: "var(--iio-text-3)", cursor: "pointer", letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "var(--iio-font-mono)" }}>{t("kopf.brotkrumeSpaces")}</button>
            <span className="iio-sep">/</span>
            <span className="iio-accent">{space.name}</span>
          </div>
          <h1>{space.name}.</h1>
          <p className="iio-lead">
            {space.description || t("kopf.leadLeer")}
          </p>
          {(() => {
            const c = cicdStatus?.spaces?.[space?.id];
            if (!c) return null;
            // Textform bewusst ausformuliert: Symbol UND Klartext, damit der
            // Zustand nicht nur an der Farbe haengt (UX-Regel "Color Only").
            const stateReady = !!c.pipelineReady;
            const stateOff = c.configured && !stateReady;
            const cls = stateReady ? "iio-space-cicd is-ready"
                       : stateOff ? "iio-space-cicd is-off"
                       : "iio-space-cicd";
            return (
              <div className={cls}>
                {stateReady ? <CicdReadyIcon/> : <CicdOffIcon/>}
                <span className="txt">
                  {stateReady ? t("kopf.cicdBereit") : t("kopf.cicdOhneGithub")}
                </span>
                {c.repo && (
                  <span className="sub">{t("kopf.cicdRepo", { repo: c.repo })}</span>
                )}
                {c.geteiltMit > 0 && (
                  /* Der Sachverhalt statt einer Warnfarbe: wer zuletzt synchronisiert, gewinnt.
                     Genau das ist am 30.07. passiert, als ein zweiter Space denselben
                     DEV-Zugang in GitHub ueberschrieben hat. */
                  <span className="warn" role="alert">
                    <CicdWarnIcon /> {t("kopf.cicdGeteilt", { count: c.geteiltMit + 1 })}
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        {isOwnerSpace && (
        <div className="iio-page-head-right iio-anim-arrival" style={{ animationDelay: "120ms" }}>
          <button className="iio-btn iio-btn--secondary" onClick={() => onSpaceUpdated && onSpaceUpdated({})}>{t("kopf.spaceBearbeiten")}</button>
          <button className="iio-btn iio-btn--signal" onClick={openCreate}>
            <PlusIcon/>{t("kopf.neueInstanz")}
          </button>
        </div>
        )}
      </div>

      {/* Grid */}
      <div className="iio-grid iio-anim-glide">
        {instances.map((inst, cardIdx) => {
          const s = stats[inst.id] || {};
          const monitor = s._errorMonitor;
          const errorsOnly = monitor?.enabled === true || monitor?.knownOnly === true
            || s.mode === "errors-only" || s._source === "error-database";
          const current = monitor?.status === "active" && monitor.historyComplete === true && !monitor.lastError;
          // A zero from incomplete or paused error collection does not establish an absence of errors.
          const failed = errorsOnly
            ? (Number.isFinite(s.failed) && (s.failed > 0 || (s.failed === 0 && current)) ? s.failed : null)
            : s.failed === null ? null : s.failed || 0;
          const artifacts = Number.isFinite(s.artifactCount) ? s.artifactCount : "-";
          const packages = !s._contentRuntimeOnly && Number.isFinite(s.packageCount) ? s.packageCount : "-";
          const tagsToShow = (inst.tags || []).slice(0, 4);
          const menuOpen = openMenuFor === inst.id;
          return (
            <article
              key={inst.id}
              className={`iio-space-card iio-anim-stagger${failed > 0 ? " iio-inst-card--has-errors" : ""}`}
              style={{ animationDelay: `${160 + Math.min(cardIdx, 8) * 40}ms` }}
              tabIndex={0}
              onClick={() => goToInstance(inst)}
              onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) goToInstance(inst); }}
            >
              {!canManage && <button className="iio-card-edit" title={t("gemeinsam:aktion.bearbeiten")}
                onClick={e => { e.stopPropagation(); openEdit(inst); }}><PencilIcon/></button>}
              {canManage && (<div className="iio-card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="iio-card-action" title={t("karte.aktionPerm")} onClick={() => runPermCheck(inst)} disabled={busyAction[inst.id] === "perm"}>
                  {busyAction[inst.id] === "perm" ? <SpinIcon/> : <ShieldIcon/>}
                </button>
                <button className="iio-card-action" title={t("karte.aktionTest")} onClick={() => testConnection(inst)} disabled={busyAction[inst.id] === "test"}>
                  {busyAction[inst.id] === "test" ? <SpinIcon/> : <TestIcon/>}
                </button>
                <button className="iio-card-action" title={t("karte.aktionPing")} onClick={() => pingEnvironments(inst)} disabled={busyAction[inst.id] === "ping"}>
                  {busyAction[inst.id] === "ping" ? <SpinIcon/> : <GlobeIcon/>}
                </button>
                <button className="iio-card-action" title={t("karte.aktionDebug")} onClick={() => reloadArtifacts(inst)} disabled={busyAction[inst.id] === "debug"}>
                  {busyAction[inst.id] === "debug" ? <SpinIcon/> : <BugIcon/>}
                </button>
                <button className="iio-card-action" title={t("karte.aktionQueues")} onClick={() => openQueues(inst)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="5" rx="1"/><rect x="3" y="14" width="18" height="5" rx="1"/><line x1="7" y1="11.5" x2="17" y2="11.5"/>
                  </svg>
                </button>
                <button className="iio-card-action" title={t("karte.aktionCockpit")} onClick={() => openCockpit(inst)}><ExternalIcon/></button>
                <button className="iio-card-action" title={t("gemeinsam:aktion.bearbeiten")} onClick={() => openEdit(inst)}><PencilIcon/></button>
                <div style={{ position: "relative" }}>
                  <button className="iio-card-action" title={t("karte.aktionMehr")} data-more-trigger onClick={() => setOpenMenuFor(menuOpen ? null : inst.id)}><MoreIcon/></button>
                  {menuOpen && (
                    <div className="iio-card-menu" onClick={(e) => e.stopPropagation()} style={{
                      position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 10,
                      background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)",
                      borderRadius: "var(--iio-r-md)",
                      boxShadow: "0 8px 24px rgba(10,20,24,.15)",
                      minWidth: 200, padding: 4,
                    }}>
                      {/* id statt Beschriftung als React-Schluessel: der Text wechselt mit der
                          Sprache, die Kennung des Eintrags nicht. */}
                      {[
                        { id: "monitor", lbl: t("karte.menuMonitor"), fn: () => { goToInstance(inst); setOpenMenuFor(null); }, color: "var(--iio-text-1)" },
                        ...(inst.accessPolicyEnforcement ? [{ id: "policies", lbl: t("karte.menuPolicies"), fn: async () => { setOpenMenuFor(null); try { const data = await api(`/api/instances/${inst.id}/access-policies`); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${inst.name}-access-policies.json`; a.click(); URL.revokeObjectURL(url); addToast(t("meldung.policiesExportiert"), "success"); } catch (e) { addToast(e.message, "error"); } }, color: "var(--iio-text-1)" }] : []),
                        ...(isOwnerSpace ? [{ id: "loeschen", lbl: t("karte.menuLoeschen"), fn: async () => { setOpenMenuFor(null); if (!window.confirm(t("karte.loeschenFrage", { name: inst.name }))) return; try { await api(`/api/instances/${inst.id}`, { method: "DELETE" }); addToast(t("meldung.instanzGeloescht"), "success"); load(); } catch (e) { addToast(e.message, "error"); } }, color: "var(--iio-critical)" }] : []),
                      ].map(item => (
                        <button key={item.id} onClick={item.fn} style={{
                          display: "block", width: "100%", textAlign: "left",
                          padding: "8px 12px", background: "transparent", border: 0,
                          font: "inherit", fontSize: 13, color: item.color,
                          cursor: "pointer", borderRadius: "var(--iio-r-sm)",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--iio-bg-3)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          {item.lbl}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>)}

              <div className="iio-card-head">
                <div className="iio-card-icon"><InstanceIcon name={inst.icon}/></div>
                <div className="iio-card-title">
                  <h3>{inst.name}</h3>
                  {tagsToShow.length > 0 && (
                    <div className="iio-meta iio-card-tags">
                      {tagsToShow.map((tag, i) => <span key={i} className="iio-card-tag">{tag}</span>)}
                    </div>
                  )}
                </div>
              </div>


              {/* Angepinnte Queues stehen an der Stelle der Beschreibung. Sind keine gepinnt,
                  bleibt die Beschreibung - der Platz wird also nicht leer belegt. */}
              {Array.isArray(inst.pinnedQueues) && inst.pinnedQueues.length > 0 ? (
                <div className="iio-card-queues" onClick={(e) => e.stopPropagation()}>
                  <div className="cq cq-head">
                    <span>{t("karte.queueSpalte")}</span><span className="r">{t("karte.queueNachrichten")}</span><span className="r">{t("karte.queueGroesse")}</span>
                  </div>
                  {inst.pinnedQueues.map(name => {
                    const st = pinStand[inst.id];
                    const q = st?.map?.[name];
                    const n = q ? q.nachrichten : null;
                    // Kein Messwert heisst kein Wert - eine 0 waere eine Aussage ueber den
                    // Fuellstand, die wir in diesem Moment nicht haben.
                    const wert = st === undefined ? "…"
                      : st?.nichtVerfuegbar ? t("karte.queueNichtVerfuegbar")
                      : q ? zahl(n) : "-";
                    const groesse = q ? (q.bytes ? fmtBytes(q.bytes) : "-") : (st === undefined ? "…" : "-");
                    return (
                      <div className={`cq${n > 0 ? " gefuellt" : ""}`} key={name}
                        title={q ? t(q.aktiv ? "karte.queueTitel" : "karte.queueTitelInaktiv", { name, nachrichten: zahl(q.nachrichten), groesse: fmtBytes(q.bytes) })
                          : st && !q && !st.nichtVerfuegbar ? t("karte.queueNichtGefunden", { name }) : name}>
                        <span className="cq-n">{name}</span>
                        <span className="r cq-v">{wert}</span>
                        <span className="r cq-s">{groesse}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`iio-card-desc${!inst.description ? " iio-card-desc--empty" : ""}`}>
                  {inst.description || t("karte.keineBeschreibung")}
                </div>
              )}

              {inst.credsIncomplete && (
                <div style={{ fontSize: 12, color: "var(--iio-warning)", fontWeight: 600, margin: "2px 0 6px" }}>
                  {t("karte.credsUnvollstaendig")}
                </div>
              )}
              {/* Welle 5 (I5): nicht eingerichtete Zusatzinstanz (V5.2). Sie ist kein Transportziel, bis sie im CI/CD Setup
                  ein GitHub Stage Kürzel hat; Instanzliste liefert cicdPending. */}
              {inst.cicdPending && (
                <Typography component="div" data-cicd-pending={inst.cicdPending}
                  sx={{ fontFamily: "inherit", fontSize: 12, lineHeight: 1.4, color: "var(--iio-warning)", fontWeight: 600, m: "2px 0 6px" }}>
                  {t("karte.cicdAusstehend")}
                </Typography>
              )}

              <div className="iio-card-foot">
                <div className="iio-card-stats iio-card-stats--divided">
                  <div className="iio-card-stat">
                    <span className="iio-stat-val">{artifacts}</span>
                    <span className="iio-stat-lbl">{t("karte.artefakte")}</span>
                  </div>
                  <div className="iio-card-stat">
                    <span className="iio-stat-val">{packages}</span>
                    <span className="iio-stat-lbl">{t("karte.pakete")}</span>
                  </div>
                  <div className="iio-card-stat">
                    <span className={`iio-stat-val${failed > 0 ? " iio-stat-val--crit" : ""}`}>{failed ?? "-"}</span>
                    <span className="iio-stat-lbl">{t("karte.fehler24h")}</span>
                  </div>
                </div>
              </div>
            </article>
          );
        })}

        {/* Werke des Space (Welle 5): eine Kachel fuer alle Leser, direkt hinter den Instanzen. */}
        <SpaceWerkeTile space={space} instances={instances}
          animationDelay={`${180 + Math.min(instances.length, 8) * 40}ms`}/>

        {/* New-instance dashed card (nur fuer Owner) */}
        {isOwnerSpace && (
        <article
          className="iio-space-card iio-space-card--new iio-anim-stagger"
          style={{ animationDelay: `${200 + Math.min(instances.length, 8) * 40}ms` }}
          tabIndex={0}
          onClick={openCreate}
          onKeyDown={(e) => { if (e.key === "Enter") openCreate(); }}
        >
          <div className="iio-plus">+</div>
          <div className="iio-lbl">{t("karte.neuTitel")}</div>
          <div className="iio-sub">{t("karte.neuSub")}</div>
        </article>
        )}
      </div>

      {/* =====================  ACTION RESULT MODAL  ===================== */}
      {actionResult && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeActionResult(); }}>
          <div className="iio-modal" role="dialog" data-focus-managed="true" aria-modal="true" style={{ maxWidth: 720 }}>
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{actionResult.label}</span>
                <h2>{actionResult.instName}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeActionResult}><CloseIcon/></button>
            </header>

            <div className="iio-modal-body">
              {actionResult.loading && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, color: "var(--iio-text-2)", fontSize: 14, padding: "16px 0" }}>
                  <div style={{ width: 16, height: 16, color: "var(--iio-brand-ink)" }}><SpinIcon/></div>
                  <span>{t("aktion.laeuft")}</span>
                </div>
              )}

              {!actionResult.loading && actionResult.error && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: "1px solid var(--iio-critical)", borderLeft: "3px solid var(--iio-critical)", borderRadius: "var(--iio-r-sm)", color: "var(--iio-critical)", fontSize: 13 }}>
                  <span style={{ width: 2, height: 13, background: "currentColor" }}/>
                  {actionResult.error}
                </div>
              )}

              {!actionResult.loading && !actionResult.error && actionResult.data && (
                <>
                  {/* TEST */}
                  {actionResult.type === "test" && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", border: `1px solid ${actionResult.data.success ? "var(--iio-success)" : "var(--iio-critical)"}`, borderLeft: `3px solid ${actionResult.data.success ? "var(--iio-success)" : "var(--iio-critical)"}`, borderRadius: "var(--iio-r-sm)", color: actionResult.data.success ? "var(--iio-success)" : "var(--iio-critical)", fontSize: 14, fontWeight: 600 }}>
                        <span style={{ width: 2, height: 14, background: "currentColor" }}/>
                        {actionResult.data.success ? t("aktion.testErfolg") : (actionResult.data.message || t("aktion.testFehler"))}
                      </div>
                      {actionResult.data.tokenObtained && <div style={{ marginTop: 12, fontSize: 13, color: "var(--iio-text-2)" }}>{t("aktion.tokenOk")}</div>}
                      {actionResult.data.tenantInfo && (
                        <div style={{ marginTop: 12, padding: 12, background: "var(--iio-bg-1)", borderRadius: "var(--iio-r-sm)", fontFamily: "var(--iio-font-mono)", fontSize: 12, color: "var(--iio-text-2)" }}>
                          {Object.entries(actionResult.data.tenantInfo).map(([k, v]) => (
                            <div key={k} style={{ marginBottom: 4 }}><span style={{ color: "var(--iio-text-3)" }}>{k}:</span> {String(v)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* PERMISSION CHECK */}
                  {actionResult.type === "perm" && (
                    <div>
                      {actionResult.data.summary && (
                        <div style={{ display: "flex", gap: 16, padding: "10px 14px", background: "var(--iio-bg-1)", borderRadius: "var(--iio-r-sm)", marginBottom: 16, fontSize: 13 }}>
                          <span style={{ color: "var(--iio-success)", fontWeight: 600 }}>{t("aktion.permOk", { anzahl: actionResult.data.summary.ok || 0 })}</span>
                          {actionResult.data.summary.denied > 0 && <span style={{ color: "var(--iio-critical)", fontWeight: 600 }}>{t("aktion.permVerweigert", { anzahl: actionResult.data.summary.denied })}</span>}
                          {actionResult.data.summary.errors > 0 && <span style={{ color: "var(--iio-warning)", fontWeight: 600 }}>{t("aktion.permFehler", { anzahl: actionResult.data.summary.errors })}</span>}
                        </div>
                      )}
                      {actionResult.data.checks && (() => {
                        // EINE Bindung fuer den Auffangwert: Gruppierung und Filter unten lesen
                        // dieselbe Variable, damit sie beim Sprachwechsel nicht auseinanderlaufen.
                        const ohneKategorie = t("aktion.permAllgemein");
                        const cats = [...new Set(actionResult.data.checks.map(c => c.category || ohneKategorie))];
                        return cats.map(cat => (
                          <div key={cat} style={{ marginBottom: 16 }}>
                            <div style={{ fontFamily: "var(--iio-font-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--iio-text-3)", marginBottom: 8 }}>{cat}</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {actionResult.data.checks.filter(c => (c.category || ohneKategorie) === cat).map((c, i) => (
                                <div key={i} className="iio-anim-stagger" style={{ animationDelay: `${Math.min(i, 8) * 30}ms`, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--iio-bg-1)", borderRadius: "var(--iio-r-sm)", fontSize: 13 }}>
                                  <span style={{ width: 2, height: 13, background: c.ok ? "var(--iio-success)" : "var(--iio-critical)", flexShrink: 0 }}/>
                                  <span style={{ flex: 1, color: "var(--iio-text-1)" }}>{c.label || c.name}</span>
                                  <span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 11, color: c.ok ? "var(--iio-success)" : "var(--iio-critical)", fontWeight: 600 }}>{c.ok ? "OK" : (c.status || "DENIED")}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                      {!actionResult.data.checks && <div style={{ fontSize: 13, color: "var(--iio-text-2)" }}>{actionResult.data.message || t("aktion.permKeineDetails")}</div>}
                    </div>
                  )}

                  {/* PING ENVIRONMENTS */}
                  {actionResult.type === "ping" && (
                    <div>
                      {actionResult.data.runtimes?.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {actionResult.data.runtimes.map((rt, i) => (
                            <div key={i} className="iio-anim-stagger" style={{ animationDelay: `${Math.min(i, 8) * 30}ms`, display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--iio-bg-1)", border: "1px solid var(--iio-border-subtle)", borderRadius: "var(--iio-r-sm)" }}>
                              <span style={{ width: 2, height: 14, background: rt.reachable ? "var(--iio-success)" : "var(--iio-critical)", flexShrink: 0 }}/>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: "var(--iio-font-mono)", fontSize: 12, color: "var(--iio-text-1)", fontWeight: 600 }}>{rt.name || rt.locationId}</div>
                                <div style={{ fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-3)", marginTop: 2 }}>{rt.url || rt.locationId} · {rt.status || (rt.reachable ? "reachable" : "unreachable")}</div>
                              </div>
                              {rt.latencyMs !== undefined && <span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-2)" }}>{rt.latencyMs} ms</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: "var(--iio-text-2)" }}>{actionResult.data.message || t("aktion.pingLeer")}</div>
                      )}
                      {actionResult.data.rawResponses && (
                        <details style={{ marginTop: 16 }}>
                          <summary style={{ cursor: "pointer", fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-3)", letterSpacing: ".14em", textTransform: "uppercase" }}>{t("aktion.pingRoh")}</summary>
                          <pre style={{ marginTop: 8, padding: 12, background: "var(--iio-bg-3)", borderRadius: "var(--iio-r-sm)", fontSize: 11, fontFamily: "var(--iio-font-mono)", overflow: "auto", maxHeight: 280, whiteSpace: "pre-wrap" }}>{JSON.stringify(actionResult.data.rawResponses, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  )}

                  {/* DEBUG ARTEFAKTE/PAKETE */}
                  {actionResult.type === "debug" && (
                    <div>
                      {actionResult.data.steps?.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                          {actionResult.data.steps.map((step, i) => (
                            <div key={i} className="iio-anim-stagger" style={{ animationDelay: `${Math.min(i, 8) * 30}ms`, display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--iio-bg-1)", borderRadius: "var(--iio-r-sm)", fontSize: 13 }}>
                              <span style={{ width: 2, height: 13, background: step.ok ? "var(--iio-success)" : (step.error ? "var(--iio-critical)" : "var(--iio-text-3)"), flexShrink: 0 }}/>
                              <span style={{ flex: 1, fontFamily: "var(--iio-font-mono)", fontSize: 12, color: "var(--iio-text-1)" }}>{step.label || step.name}</span>
                              {step.count !== undefined && <span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-2)" }}>{step.count}</span>}
                              {step.error && <span style={{ fontSize: 11, color: "var(--iio-critical)" }}>{step.error}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {actionResult.data.result && (
                        <div style={{ display: "flex", gap: 24, padding: "12px 16px", background: "var(--iio-bg-1)", borderLeft: "3px solid var(--iio-success)", borderRadius: "var(--iio-r-sm)", fontSize: 14 }}>
                          <div><span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--iio-text-3)", display: "block", marginBottom: 4 }}>{t("aktion.debugPakete")}</span><span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 18, fontWeight: 600, color: "var(--iio-text-1)" }}>{actionResult.data.result.runtimeOnly ? "-" : actionResult.data.result.packageCount ?? "-"}</span></div>
                          <div><span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--iio-text-3)", display: "block", marginBottom: 4 }}>{t("aktion.debugArtefakte")}</span><span style={{ fontFamily: "var(--iio-font-mono)", fontSize: 18, fontWeight: 600, color: "var(--iio-text-1)" }}>{actionResult.data.result.artifactCount || 0}</span></div>
                        </div>
                      )}
                      <details style={{ marginTop: 16 }}>
                        <summary style={{ cursor: "pointer", fontFamily: "var(--iio-font-mono)", fontSize: 11, color: "var(--iio-text-3)", letterSpacing: ".14em", textTransform: "uppercase" }}>{t("aktion.debugRoh")}</summary>
                        <pre style={{ marginTop: 8, padding: 12, background: "var(--iio-bg-3)", borderRadius: "var(--iio-r-sm)", fontSize: 11, fontFamily: "var(--iio-font-mono)", overflow: "auto", maxHeight: 280, whiteSpace: "pre-wrap" }}>{JSON.stringify(actionResult.data, null, 2)}</pre>
                      </details>
                    </div>
                  )}
                </>
              )}
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left"/>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeActionResult}>{t("gemeinsam:aktion.schliessen")}</button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* =====================  CREATE / EDIT MODAL  ===================== */}
      {(showCreate || editing) && (
        <div className="iio-modal-overlay is-open" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="iio-modal" role="dialog" data-focus-managed="true" aria-modal="true" style={{ maxWidth: 640 }}>
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{editing ? t("gemeinsam:aktion.bearbeiten") : t("gemeinsam:aktion.anlegen")}</span>
                <h2>{editing ? t("dialog.titelBearbeiten", { name: editing.name }) : t("dialog.titelNeu")}</h2>
              </div>
              <button className="iio-modal-close" onClick={closeModal}><CloseIcon/></button>
            </header>

            {canManage && <div className="iio-modal-tabs">
              {[
                ...(!editing ? [{ key: "json", lbl: t("dialog.tabJson") }] : []),
                { key: "connection", lbl: t("dialog.tabVerbindung") },
                { key: "edge", lbl: t("dialog.tabEdge") },
                { key: "ai", lbl: t("dialog.tabAi") },
                { key: "policies", lbl: t("dialog.tabPolicies") },
              ].map(reiter => (
                <button key={reiter.key} type="button" className={`iio-modal-tab${modalTab === reiter.key ? " is-active" : ""}`} onClick={() => setModalTab(reiter.key)}>
                  {reiter.lbl}
                </button>
              ))}
            </div>}

            <div className="iio-modal-body">
              <div className="iio-field">
                <span className="iio-field-label">{t("spaces:feld.symbol")}</span>
                <IconPicker value={form.icon} onChange={icon => setField("icon", icon)} disabled={submitting}/>
                {editing && <div className="iio-field-hint">{t("spaces:bearbeiten.symbolPersoenlich")}</div>}
              </div>
              {canManage && <>
              {modalTab === "json" && !editing && <>
                <div className="iio-field">
                  <span className="iio-field-label">{t("json.titel")}</span>
                  <div style={{fontSize:12,color:"var(--iio-text-2)",marginBottom:10,lineHeight:1.5}}>
                    {t("json.info")}
                  </div>
                  <textarea className="iio-textarea" style={{minHeight:180,fontSize:12,lineHeight:1.6}}
                    placeholder={'{\n  "oauth": {\n    "clientid": "sb-...",\n    "clientsecret": "...",\n    "url": "https://...",\n    "tokenurl": "https://..."\n  }\n}'}
                    onPaste={e => {
                      const text = e.clipboardData.getData("text");
                      try {
                        const parsed = JSON.parse(text);
                        const o = parsed.oauth || parsed;
                        if (o.url) setField("tenantUrl", String(o.url).replace(/\/+$/, ""));
                        if (o.clientid || o.clientId) setField("clientId", o.clientid || o.clientId);
                        if (o.clientsecret || o.clientSecret) setField("clientSecret", o.clientsecret || o.clientSecret);
                        if (o.tokenurl || o.tokenUrl) setField("tokenUrl", o.tokenurl || o.tokenUrl);
                        setField("authType", "oauth");
                        addToast(t("meldung.serviceKeyImportiert"), "success");
                        setModalTab("connection");
                        e.preventDefault();
                      } catch {
                        addToast(t("meldung.serviceKeyUngueltig"), "error");
                      }
                    }}/>
                </div>
              </>}

              {modalTab === "connection" && <>
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-name">{t("verbindung.name")}</label>
                  <input id="iio-inst-name" className="iio-input" autoFocus value={form.name} onChange={e => setField("name", e.target.value)} placeholder={t("verbindung.namePlatzhalter")}/>
                </div>
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-desc">{t("verbindung.beschreibung")}</label>
                  <textarea id="iio-inst-desc" className="iio-textarea" value={form.description} onChange={e => setField("description", e.target.value)} placeholder={t("verbindung.beschreibungPlatzhalter")}/>
                  <div className="iio-field-hint">{t("verbindung.beschreibungHinweis")}</div>
                </div>
                <div className="iio-field">
                  <span className="iio-field-label">{t("verbindung.tags")}</span>
                  <div className="iio-fields-3">
                    {[0, 1, 2, 3].map(i => (
                      <input key={i} className="iio-input" value={(form.tags || [])[i] || ""} onChange={e => setTag(i, e.target.value)}
                        onBlur={e => {
                          // Ein Stufenname hier gehoert ins Dropdown. Beim Verlassen des Feldes
                          // wandert er dorthin, statt bis zum Speichern als zweiter, gleichzeitig
                          // gefuellter Weg stehen zu bleiben.
                          const stufe = TAG_ZU_STUFE[String(e.target.value || "").trim().toLowerCase()];
                          if (stufe) { setTag(i, ""); setField("stufe", stufe); }
                        }}
                        placeholder={t(`verbindung.tagPlatzhalter${i + 1}`)}/>
                    ))}
                  </div>
                  {/* Ein Stufenname im Sachtag-Feld wird beim Speichern entfernt - das darf
                      nicht stillschweigend passieren, sonst tippt jemand "dev" und wundert
                      sich, wohin es verschwunden ist. Die Stufe hat ihr eigenes Feld. */}
                  <div className="iio-field-hint">{t("verbindung.tagsHinweis")}</div>
                </div>
                {/* Die Stufe ist eine Auswahl aus vier festen Werten - deshalb ein Dropdown und
                    kein Freitext. Damit ist die Regel "genau eine Stufe" strukturell erzwungen,
                    statt sie erst beim Speichern abzuweisen. */}
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-stufe">{t("verbindung.stufe")}</label>
                  <select id="iio-inst-stufe" className="iio-input" value={form.stufe || ""} onChange={e => setField("stufe", e.target.value)} style={{ maxWidth: 320 }}>
                    <option value="">{t("verbindung.stufeKeine")}</option>
                    {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                  </select>
                  <div className="iio-field-hint">
                    {t("verbindung.stufeHinweis")}
                    {form.stufe && cicdStatus?.spaces?.[space?.id]?.configured && (
                      <span style={{ display: "block", marginTop: 4 }}>
                        <Trans t={t} i18nKey="verbindung.stufeEnv" values={{ env: stageLabel(form.stufe) }} components={{ b: <b/> }}/>
                      </span>
                    )}
                  </div>
                </div>
                <div className="iio-field">
                  <label className="iio-field-label" htmlFor="iio-inst-region">{t("instance.region.label")}</label>
                  <input id="iio-inst-region" className="iio-input" value={form.region || ""} maxLength={20} onChange={e => setField("region", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt}
                    placeholder={t("instance.region.placeholder")} aria-describedby="iio-inst-region-hint" style={{ maxWidth: 320 }}/>
                  <div className="iio-field-hint" id="iio-inst-region-hint">{t(istGeerbt ? "instance.region.inherited" : "instance.region.hint")}</div>
                </div>
                <div className="iio-field">
                  <span className="iio-field-label">{t("verbindung.auth")}</span>
                  <div className="iio-auth-toggle">
                    <button type="button" disabled={istGeerbt} className={form.authType === "oauth" ? "is-active" : ""} onClick={() => setField("authType", "oauth")}>{t("verbindung.authOauth")}</button>
                    <button type="button" disabled={istGeerbt} className={form.authType === "basic" ? "is-active" : ""} onClick={() => setField("authType", "basic")}>{t("verbindung.authBasic")}</button>
                  </div>
                </div>
                {/* Aus einer Vorlage abgeleitet: die Verbindung gehört der Vorlage. Der Hinweis
                    steht VOR den gesperrten Feldern, damit niemand erst tippt und dann merkt,
                    dass nichts passiert. */}
                {istGeerbt && (
                  <div className="iio-field-hint" style={{ marginTop: -4, marginBottom: 8, padding: "8px 10px", borderLeft: "3px solid var(--iio-info)", background: "color-mix(in srgb, var(--iio-info) 5%, var(--iio-bg-1))", borderRadius: "var(--iio-r-sm)", color: "var(--iio-text-2)" }}>
                    {form.vorlageName
                      ? <Trans t={t} i18nKey="verbindung.geerbt" values={{ name: form.vorlageName }} components={{ b: <b/> }}/>
                      : t("verbindung.geerbtOhneName")}
                  </div>
                )}
                <div className="iio-field">
                  <label className="iio-field-label">{t("verbindung.tenantUrl")}</label>
                  <input className="iio-input iio-input--mono" value={form.tenantUrl} onChange={e => setField("tenantUrl", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={t("verbindung.tenantUrlPlatzhalter")}/>
                </div>
                {form.authType === "oauth" ? <>
                  <div className="iio-field">
                    <label className="iio-field-label">{t("verbindung.tokenUrl")}</label>
                    <input className="iio-input iio-input--mono" value={form.tokenUrl} onChange={e => setField("tokenUrl", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={t("verbindung.tokenUrlPlatzhalter")}/>
                  </div>
                  <div className="iio-fields-2">
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.clientId")}</label>
                      <input className="iio-input iio-input--mono" value={form.clientId} onChange={e => setField("clientId", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt}/>
                    </div>
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.clientSecret")}</label>
                      <input className="iio-input iio-input--mono" type="password" value={form.clientSecret} onChange={e => setField("clientSecret", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={editing ? t("verbindung.unveraendert") : ""}/>
                    </div>
                  </div>
                </> : <>
                  <div className="iio-fields-2">
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.benutzername")}</label>
                      <input className="iio-input iio-input--mono" value={form.username} onChange={e => setField("username", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt}/>
                    </div>
                    <div className="iio-field">
                      <label className="iio-field-label">{t("verbindung.passwort")}</label>
                      <input className="iio-input iio-input--mono" type="password" value={form.password} onChange={e => setField("password", e.target.value)} disabled={istGeerbt} readOnly={istGeerbt} placeholder={editing ? t("verbindung.unveraendert") : ""}/>
                    </div>
                  </div>
                </>}
                <div className="iio-field">
                  <label className="iio-field-label">{t("verbindung.cockpitUrl")}</label>
                  <input className="iio-input iio-input--mono" value={form.suiteUrl} onChange={e => setField("suiteUrl", e.target.value)} placeholder={t("verbindung.cockpitUrlPlatzhalter")}/>
                </div>
                {/* Das Monitor-Zeitfenster ist nicht mehr einstellbar: 24 Stunden sind als
                    Höchstwert zugesagt, und der Zähl-Worker haelt ohnehin nur die Fenster
                    1 h, 6 h und 24 h vor. Der gespeicherte Wert bleibt im Formular und wird
                    unveraendert weitergereicht, damit nichts still auf 24 springt.
                    Wieder sichtbar machen: den Block unten einkommentieren und die Cockpit URL
                    zurueck in ein zweispaltiges Raster (iio-fields-2) stellen.
                <div className="iio-field">
                  <label className="iio-field-label">Monitor (h)</label>
                  <input className="iio-input" type="number" min="1" max="168" value={form.monitorHours} onChange={e => setField("monitorHours", Number(e.target.value) || 24)} disabled={istGeerbt} readOnly={istGeerbt}/>
                </div>
                */}
                {/* Der Haken stand bis Welle 5 im Tab „Werke“. Die Werke pflegt man seit Welle 5 am Space
                    (Kachel „Werke“ auf dieser Seite), der Haken gehört zur Instanz und bleibt hier. */}
                <div className="iio-field">
                  <div className="ci-pipeline-option">
                    <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
                      <input type="checkbox" checked={form.hasPipelineConcept} onChange={e => setField("hasPipelineConcept", e.target.checked)}/>
                      <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 3 }}>
                        <Trans t={t} i18nKey="werke.pipelineLabel" components={{
                          pfeil: <span className="iio-mon2-chevflow iio-mon2-chevflow--flow iio-mon2-chevflow--sm" aria-hidden="true"><i/><i/></span>,
                        }}/>
                      </span>
                    </label>
                    <details className="ci-inline-help">
                      <summary>{t("werke.pipelineAria")}</summary>
                      <p>{t("werke.pipelineInfo")}</p>
                    </details>
                  </div>
                </div>
                {/* Der Monitor-Worker hat keinen Modusschalter mehr: es gibt nur noch den
                    lastadaptiven Zähl-Motor. Bei geerbten Instanzen bleibt der Hinweis,
                    WO der Monitor läuft - das ist eine echte Information, kein Bedienelement. */}
                {istGeerbt && <div className="iio-field">
                  <div style={{fontSize:11,color:"var(--iio-text-3)",lineHeight:1.4}}>
                    {form.vorlageName
                      ? <Trans t={t} i18nKey="verbindung.workerGeerbt" values={{ name: form.vorlageName }} components={{ b: <b/> }}/>
                      : t("verbindung.workerGeerbtOhneName")}
                  </div>
                </div>}
              </>}

              {modalTab === "edge" && <>
                <RuntimeLocationsField value={form.runtimeLocations}
                  inherited={!!(form.serviceKeyVon || form.runtimeSource || form.runtimeInheritanceError)}
                  sourceName={form.runtimeSource?.name || form.vorlageName} error={form.runtimeInheritanceError}
                  onChange={value => setField("runtimeLocations", value)}/>
                {/* Das Verproben des Edge-Deploys wird nicht mehr gebraucht. Der Testlauf
                    deployte einen echten iFlow gegen den Tenant, nur um die Erreichbarkeit zu
                    zeigen. Die Komponente EdgeDeployTester steht weiter oben in dieser Datei und
                    ruft ausschliesslich Routen auf, die auch anderswo benutzt werden - es faellt
                    also nichts weg, was noch jemand braucht.
                {editing
                  ? <EdgeDeployTester instanceId={editing.id} runtimeLocations={form.runtimeLocations} />
                  : <div className="iio-field" style={{marginTop:4,paddingTop:16,borderTop:"1px solid var(--iio-border)"}}>
                      <span className="iio-field-label">Edge-Deploy verproben</span>
                      <div className="iio-field-hint" style={{marginTop:6}}>Instanz zuerst anlegen und speichern - danach kann hier ein iFlow testweise in die Edge deployt werden.</div>
                    </div>}
                */}
                <div className="iio-field" style={{marginTop:4,paddingTop:16,borderTop:"1px solid var(--iio-border)"}}>
                  <span className="iio-field-label">{t("edge.monTitel")}</span>
                  <div style={{fontSize:12,color:"var(--iio-text-2)",marginBottom:8,lineHeight:1.5}}>
                    {t("edge.monInfo")}
                  </div>
                  {(Array.isArray(form.edgeMonitoring) ? form.edgeMonitoring : []).map((em, idx) => {
                    const offen = edgeMonOffen === idx;
                    return (
                      <div key={idx} className={`iio-edgemon${offen ? " is-open" : ""}`}>
                        {/* Kopfzeile: Aufklapp-Button und Entfernen sind Geschwister -
                            verschachtelte Buttons waeren ungueltiges HTML. */}
                        <div className="iio-edgemon-kopf">
                          <button type="button" className="iio-edgemon-toggle" aria-expanded={offen}
                            onClick={() => setEdgeMonOffen(offen ? null : idx)}>
                            <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>
                            <span className="ttl">{(em.name || "").trim() || t("edge.monUnbenannt")}</span>
                            {!offen && <span className="sub">{(em.url || "").trim() || t("edge.monOhneAdresse")}</span>}
                          </button>
                          <button type="button" className="iio-edgemon-x" onClick={() => {
                            setField("edgeMonitoring", form.edgeMonitoring.filter((_, i) => i !== idx));
                            setEdgeMonOffen(o => o === idx ? null : (o != null && o > idx ? o - 1 : o));
                          }} title={t("edge.monEntfernen")}>x</button>
                        </div>
                        {offen && <div className="iio-edgemon-body">
                          <div className="iio-field">
                            <label className="iio-field-label" htmlFor={`iio-edgemon-name-${idx}`}>{t("edge.monName")}</label>
                            <input id={`iio-edgemon-name-${idx}`} className="iio-input" value={em.name || ""}
                              onChange={e => setEdgeMonFeld(idx, "name", e.target.value)} placeholder={t("edge.monNamePlatzhalter")}/>
                            <div className="iio-field-hint">{t("edge.monNameHinweis")}</div>
                          </div>
                          <div className="iio-field">
                            <label className="iio-field-label" htmlFor={`iio-edgemon-url-${idx}`}>{t("edge.monUrl")}</label>
                            <input id={`iio-edgemon-url-${idx}`} className="iio-input" value={em.url || ""}
                              onChange={e => setEdgeMonFeld(idx, "url", e.target.value)} placeholder="https://edge.example.com"/>
                            <div className="iio-field-hint">{t("edge.monUrlHinweis")}</div>
                          </div>
                          <div className="iio-field">
                            <span className="iio-field-label">{t("edge.monKey")}</span>
                            <textarea className="iio-textarea" style={{minHeight:64,fontSize:12,lineHeight:1.6}}
                              placeholder={'{ "clientid": "sb-...", "clientsecret": "..." }'}
                              onPaste={e => {
                                const text = e.clipboardData.getData("text");
                                try {
                                  const parsed = JSON.parse(text);
                                  const o = parsed.oauth || parsed;
                                  const cid = o.clientid || o.clientId, sec = o.clientsecret || o.clientSecret;
                                  // tokenurl still mitnehmen: Basic braucht sie nicht, aber eine
                                  // spaetere OAuth-Anbindung soll ohne erneutes Einfuegen auskommen.
                                  const tok = o.tokenurl || o.tokenUrl;
                                  if (!cid && !sec) throw new Error("leer");
                                  setForm(prev => {
                                    const next = [...(prev.edgeMonitoring || [])];
                                    next[idx] = { ...next[idx], ...(cid ? { clientId: cid } : {}), ...(sec ? { clientSecret: sec } : {}), ...(tok ? { tokenUrl: tok } : {}) };
                                    return { ...prev, edgeMonitoring: next };
                                  });
                                  addToast(t("meldung.serviceKeyImportiert"), "success");
                                  e.preventDefault();
                                } catch {
                                  addToast(t("meldung.serviceKeyUngueltig"), "error");
                                }
                              }}/>
                            <div className="iio-field-hint">{t("edge.monKeyHinweis")}</div>
                          </div>
                          <div className="iio-fields-2">
                            <div className="iio-field" style={{margin:0}}>
                              <label className="iio-field-label" htmlFor={`iio-edgemon-cid-${idx}`}>{t("verbindung.clientId")}</label>
                              <input id={`iio-edgemon-cid-${idx}`} className="iio-input" value={em.clientId || ""}
                                onChange={e => setEdgeMonFeld(idx, "clientId", e.target.value)} placeholder="sb-..."/>
                            </div>
                            <div className="iio-field" style={{margin:0}}>
                              <label className="iio-field-label" htmlFor={`iio-edgemon-sec-${idx}`}>{t("verbindung.clientSecret")}</label>
                              <input id={`iio-edgemon-sec-${idx}`} className="iio-input" type="password" value={em.clientSecret || ""}
                                onChange={e => setEdgeMonFeld(idx, "clientSecret", e.target.value)}
                                placeholder={em.hasSecret ? t("verbindung.unveraendert") : ""}/>
                            </div>
                          </div>
                        </div>}
                      </div>
                    );
                  })}
                  <button type="button" onClick={() => {
                    const naechster = (form.edgeMonitoring || []).length;
                    setField("edgeMonitoring", [...(form.edgeMonitoring || []), { name: "", url: "", clientId: "", clientSecret: "" }]);
                    setEdgeMonOffen(naechster);
                  }} style={{
                    background:"none",border:"1px dashed var(--iio-border)",borderRadius:"var(--iio-r-sm)",
                    padding:"6px 14px",fontSize:12,color:"var(--iio-text-2)",cursor:"pointer",fontFamily:"var(--iio-font-sans)",marginTop:2,
                  }}>{t("edge.monHinzufuegen")}</button>
                  <div className="iio-field-hint" style={{marginTop:8}}>{t("edge.monHinweis")}</div>
                </div>
              </>}

              {modalTab === "ai" && <>
                <div className="iio-field">
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
                    <input type="checkbox" checked={form.aiCapabilities?.enabled || false} onChange={e => setField("aiCapabilities", { ...form.aiCapabilities, enabled: e.target.checked })}/>
                    {t("ai.aktivieren")}
                  </label>
                  <div className="iio-field-hint">{t("ai.hinweis")}</div>
                </div>
                {form.aiCapabilities?.enabled && (
                  <div className="iio-field">
                    <span className="iio-field-label">{t("ai.aktionen")}</span>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[
                        { key: "read", lbl: t("ai.read") },
                        { key: "monitor", lbl: t("ai.monitor") },
                        { key: "test", lbl: t("ai.test") },
                        { key: "write", lbl: t("ai.write") },
                        { key: "deploy", lbl: t("ai.deploy") },
                        { key: "tpm", lbl: t("ai.tpm") },
                      ].map(f => (
                        <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--iio-text-2)" }}>
                          <input type="checkbox" checked={form.aiCapabilities?.features?.[f.key] || false}
                            onChange={e => setField("aiCapabilities", { ...form.aiCapabilities, features: { ...form.aiCapabilities?.features, [f.key]: e.target.checked } })}/>
                          {f.lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>}

              {modalTab === "policies" && <>
                {editing && istGeerbt && <PolicyCredentialSource instanceId={editing.id}
                  value={form.policyCredentialSource || null} invalid={!!form.policyInheritanceError}
                  onChange={binding => setForm(previous => ({ ...previous, policyCredentialSource: binding, policyInheritanceError: null,
                    policyTargetAuth: !!binding, policyCredentials: [], policyUsername: "", policyPassword: "", policyTransportKey: null }))} />}
                <div className="iio-field">
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
                    <input type="checkbox" checked={form.accessPolicyEnforcement} onChange={e => setField("accessPolicyEnforcement", e.target.checked)}/>
                    {t("policies.enforcement")}
                  </label>
                  <div className="iio-field-hint">{t("policies.enforcementHinweis")}</div>
                </div>
                <div style={{height:1,background:"var(--iio-border)",margin:"16px 0"}}/>
                {!form.policyCredentialSource && <div className="iio-field">
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14 }}>
                    <input type="checkbox" checked={form.policyTargetAuth} onChange={e => setField("policyTargetAuth", e.target.checked)} style={{accentColor:"var(--iio-signal)"}}/>
                    {t("policies.userCreds")}
                  </label>
                  <div className="iio-field-hint">{t("policies.userCredsHinweis")}</div>
                </div>}
                {!form.policyCredentialSource && (form.policyTargetAuth || form.policyCredentials?.length > 0) &&
                  <PolicyCredentials credentials={form.policyCredentials || []} selectedKey={form.policyTransportKey}
                    editing={!!editing} active={form.policyTargetAuth} onChange={(credentials, key) => setForm(previous => ({ ...previous,
                      policyCredentials: credentials, policyTransportKey: key }))} />}
                {editing && (
                  <div className="iio-field" style={{marginTop:16}}>
                    <span className="iio-field-label">{t("policies.testTitel")}</span>
                    <button type="button" className="iio-btn iio-btn--secondary" disabled={testingInModal} onClick={async () => {
                      setTestingInModal(true);
                      try {
                        const r = await api(`/api/instances/${editing.id}/test`, { method: "POST" });
                        addToast(r.success ? t("meldung.verbindungOk") : (r.message || t("gemeinsam:zustand.fehler")), r.success ? "success" : "error");
                      } catch (e) { addToast(e.message, "error"); }
                      setTestingInModal(false);
                    }}>{testingInModal ? t("policies.testLaeuft") : t("policies.testJetzt")}</button>
                  </div>
                )}
              </>}

              {/* Beim Speichern gehen die Zugangsdaten in ein GitHub-Repository. Das gehoert
                  ANGEKUENDIGT, bevor es passiert - eine Meldung hinterher waere eine Nachricht
                  ueber eine Entscheidung, die der Nutzer nicht mehr treffen konnte. Steht am
                  Ende des Formulars und nicht im Fuss, weil dort beim Bearbeiten schon das
                  Loeschfeld sitzt. Nur sichtbar, wenn wirklich uebertragen wird: Space gebunden
                  UND Instanz mit Stufen-Tag. */}
              {cicdStatus?.spaces?.[space?.id]?.configured && form.stufe && (
                <div className="iio-field-hint" style={{ marginTop: 14, lineHeight: 1.5 }}>
                  <Trans t={t} i18nKey="dialog.cicdHinweis" values={{ env: stageLabel(form.stufe) }} components={{ b: <b/> }}/>
                </div>
              )}
              </>}
            </div>

            <footer className="iio-modal-foot">
              <div className="iio-modal-foot-left">
                {isOwnerSpace && <>
                {editing && delConfirm === editing.name ? (
                  <button className="iio-btn iio-btn--danger" onClick={submitDelete}>{t("dialog.loeschenEndgueltig")}</button>
                ) : editing ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input className="iio-input" style={{ width: 200, height: 32 }} value={delConfirm} onChange={e => setDelConfirm(e.target.value)} placeholder={t("dialog.loeschenPlatzhalter", { name: editing.name })}/>
                  </div>
                ) : null}
                </>}
              </div>
              <div className="iio-modal-foot-right">
                <button className="iio-btn iio-btn--secondary" onClick={closeModal}>{t("gemeinsam:aktion.abbrechen")}</button>
                <button className="iio-btn iio-btn--signal" onClick={submitForm} disabled={submitting || (!onlyIconEdit && (!form.name.trim() || !form.tenantUrl.trim()))}>
                  {/* Beim Speichern mit CI/CD-Bindung laufen mehrere GitHub-Aufrufe, das dauert
                      ein paar Sekunden laenger. "Speichere…" liesse den Nutzer raten, ob es
                      haengt - die Beschriftung sagt deshalb, worauf gewartet wird. */}
                  {submitting
                    ? (!onlyIconEdit && cicdStatus?.spaces?.[space?.id]?.configured && form.stufe
                        ? t("dialog.speichertUndUebertraegt")
                        : t("dialog.speichert"))
                    : editing ? t("gemeinsam:aktion.speichern") : t("dialog.instanzAnlegen")}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
      {/* JMS-Queues als Tabelle. Natives .iio-modal (kein MUI-Portal, das verliert den
          .iio-Scope). Sortiert nach Fuellstand, weil eine gefuellte Queue die Information ist,
          nach der gesucht wird - auf grossen Tenants sind es ueber 150 Zeilen. */}
      {queueModal && (()=>{
        const d = queueModal.data;
        const alle = d?.queues || [];
        const f = queueFilter.trim().toLowerCase();
        const liste = alle.filter(q => (!nurGefuellte || q.nachrichten > 0) && (!f || q.name.toLowerCase().includes(f)));
        const kb = fmtBytes;
        return (
        <div className="iio-modal-overlay is-open" onClick={e=>{if(e.target===e.currentTarget)setQueueModal(null);}}>
          <div className="iio iio-modal iio-queue-modal" role="dialog" data-focus-managed="true" aria-modal="true">
            <header className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{queueModal.inst.name}</span>
                <h2>{t("queues.titel")}</h2>
              </div>
              <button className="iio-modal-close" onClick={()=>setQueueModal(null)} title={t("gemeinsam:aktion.schliessen")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </header>
            <div className="iio-modal-body">
              {queueModal.loading && <div className="iio-q-note">{t("queues.laedt")}</div>}
              {queueModal.error && <div className="iio-q-note">{t("queues.fehler", { grund: queueModal.error })}</div>}
              {d?.nichtVerfuegbar && <div className="iio-q-note">{d.grund}</div>}
              {d && !d.nichtVerfuegbar && (<>
                <div className="iio-q-sum">
                  <span><b>{zahl(d.anzahl)}</b> {t("queues.summeQueues")}</span>
                  <span><b>{zahl(d.nachrichtenGesamt)}</b> {t("queues.summeNachrichten")}</span>
                  <span><b>{kb(d.bytesGesamt)}</b> {t("queues.summeBelegt")}</span>
                  {d.inaktiv > 0 && <span className="warn"><b>{zahl(d.inaktiv)}</b> {t("queues.summeInaktiv")}</span>}
                </div>
                <div className="iio-q-bar">
                  <input className="iio-input" placeholder={t("queues.filterPlatzhalter")} value={queueFilter}
                    onChange={e=>setQueueFilter(e.target.value)}/>
                  <label className="iio-q-check">
                    <input type="checkbox" checked={nurGefuellte} onChange={e=>setNurGefuellte(e.target.checked)}/>
                    <span>{t("queues.nurGefuellte")}</span>
                  </label>
                </div>
                <div className="iio-qtab">
                  <div className="qhead">
                    <span>{t("queues.spalteQueue")}</span><span className="r">{t("queues.spalteNachrichten")}</span><span className="r">{t("queues.spalteGroesse")}</span><span className="r">{t("queues.spalteZustand")}</span>
                  </div>
                  {liste.map(q => {
                    const gewaehlt = queueAuswahl.includes(q.name);
                    const voll = queueAuswahl.length >= QUEUE_MAX && !gewaehlt;
                    return (
                    <div className={`qrow${q.nachrichten > 0 ? " gefuellt" : ""}${gewaehlt ? " gewaehlt" : ""}`} key={q.name}>
                      <button type="button" className={`qn qn-btn${voll ? " voll" : ""}`} onClick={() => toggleQueue(q.name)}
                        disabled={voll}
                        title={gewaehlt ? t("queues.abwaehlen") : voll ? t("queues.voll", { max: QUEUE_MAX }) : t("queues.anpinnen")}>
                        <span className="qn-mark" aria-hidden="true">{gewaehlt ? "\u2713" : ""}</span>
                        <span className="qn-txt">{q.name}</span>
                      </button>
                      <span className="r qv">{zahl(q.nachrichten)}</span>
                      <span className="r qs">{q.bytes ? kb(q.bytes) : "-"}</span>
                      <span className={`r qz${q.aktiv ? "" : " inaktiv"}`}>{q.aktiv ? t("queues.aktiv") : t("queues.inaktiv")}</span>
                    </div>
                    );
                  })}
                  {!liste.length && <div className="iio-q-note">{t("queues.keinTreffer")}</div>}
                </div>
                {liste.length !== alle.length && (
                  <div className="iio-q-note">{t("queues.gefiltert", { sichtbar: liste.length, gesamt: alle.length })}</div>
                )}
              </>)}
            </div>
            {d && !d.nichtVerfuegbar && (
              <footer className="iio-modal-foot">
                <div className="iio-modal-foot-left">
                  <span className="iio-q-note">
                    {t(queueAuswahl.length >= QUEUE_MAX ? "queues.auswahlVoll" : "queues.auswahl", { anzahl: queueAuswahl.length, max: QUEUE_MAX })}
                  </span>
                </div>
                <div className="iio-modal-foot-right">
                  <button className="iio-btn iio-btn--secondary" onClick={()=>setQueueModal(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
                  <button className="iio-btn iio-btn--signal" onClick={speichereQueues} disabled={queueSpeichert}>
                    {queueSpeichert ? t("dialog.speichert") : t("gemeinsam:aktion.speichern")}
                  </button>
                </div>
              </footer>
            )}
          </div>
        </div>
        );
      })()}
    </>
  );
};

export default SpaceDetail;
